import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AddOptionsMenuService,
  GetMenus,
  UpdateOptionsMenuService,
} from "services";
import { useFormik } from "formik";
import * as Yup from "yup";
import { OptionI } from "interfaces";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOptionsMenus } from "../../store";
import { useTranslation } from "react-i18next";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { OptionSelect } from "Common/Components";
import {
  getDefaultValueForSelect,
  validateUrlPath,
} from "helpers/functionsHelpers";

export const useAddOptions = () => {
  const navigate = useNavigate();
  const [isSubmitOption, setIsSubmitOption] = useState(false);
  const { t } = useTranslation();
  const [valueMenu, setValueMenu] = useState<OptionSelect>();
  const { handleCloseModal, option, isEdit, setIsEdit, setOption } =
    useOptionsMenus();
  const noSpecialCharactersRegex = /^[a-zA-ZñÑ0-9\s.\-\/]+$/;
  const { data: dataMenus } = useQuery({
    queryKey: ["security-service-menus"],
    queryFn: GetMenus,
    staleTime: 1000 * 60 * 60,
  });
  const [optionSelect, setOptionSelect] = useState<OptionSelect[]>([
    {
      label: t("selectComponent.defaultValue"),
      value: "0",
      isDisabled: true,
    },
  ]);


  const viewAppOptions: OptionSelect[] = [
    { label: t('createOption.yes'), value: "1" },
    { label:  t('createOption.not'), value: "0" },
  ];


  useEffect(() => {
    if (dataMenus) {
      const options: OptionSelect[] = [];

      dataMenus.data.map((menu) => {
        options.push({
          label: menu.description,
          value: menu.id.toString(),
        });
      });

      setOptionSelect(options);
    }
  }, [dataMenus]);
  const queryClient = useQueryClient();

  const initialValues = {
    description: option?.description ?? "",
    url: option?.url ?? "",
    menu_id: option?.mainmenu.id ?? "",
    viewapp: option?.viewapp?.toString() ?? "0",
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string()
      .matches(
        noSpecialCharactersRegex,
        t("createOption.form.errorDescriptionTwo")
      )
      .required(t("createOption.form.errorDescription")),
    url: validateUrlPath(t),
    menu_id: Yup.number().required(t("createOption.form.errorMenu")),
    viewapp: Yup.number().required(t("createOption.form.errorMenu")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        setSubmitting(false);

        return;
      }
      setSubmitting(true);

      const payload: OptionI = {
        description: values.description,
        url: values.url,
        menu_id: values.menu_id,
        viewapp: values.viewapp,
      };

      try {
        if (isEdit && option) {
          await handleEditOption(payload, option.id)
        } else {
          await handleCreateOption(payload)
        }
      } catch (error) {
        showNotifications(NotificationType.ERROR, t("notificationsMessages.error"))
      } finally {
        setSubmitting(false);
      }

      // if (isEdit) {
      //   // Update option
      //   handleEditOption(option);
      // } else {
      //   // Create option
      //   handleCreateOption(option);
      // }
    },
  });

  useEffect(() => {
    const defaultValueFind = getDefaultValueForSelect(
      optionSelect,
      formik.values.menu_id.toString()
    );

    setValueMenu(defaultValueFind);
  }, [formik.values.menu_id, optionSelect]);

  const handleCreateOption = async (payload: OptionI) => {
    try {
      const response = await AddOptionsMenuService(payload);

      if (response.code === "200") {
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.successAdd")
        );
        queryClient.invalidateQueries({
          queryKey: ["security-service-options-menus"],
        });
        setOption(null);
        handleCloseModal();
        formik.resetForm();
      } else {
        setOption(null);
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error") + response.message
        );
      }
    } catch (error) {
      setOption(null);
      showNotifications(
        NotificationType.ERROR,
        t("notificationsMessages.error") + error
      );
    }
  };

  const handleEditOption = async (payload: OptionI, id: number) => {
    try {
      if (!option) return;

      const response = await UpdateOptionsMenuService(id, payload);

      if (response.code === "200") {
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.success")
        );
        setIsEdit(false);
        setOption(null);
        queryClient.invalidateQueries({
          queryKey: ["security-service-options-menus"],
        });
        handleCloseModal();
      } else {
        setIsEdit(false);
        setOption(null);
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error") + response.message
        );
      }
    } catch (error) {
      setIsEdit(false);
      setOption(null);
      showNotifications(
        NotificationType.ERROR,
        t("notificationsMessages.error") + error
      );
    }
  };

  return {
    navigate,
    formik,
    isSubmitOption,
    listMenus: optionSelect,
    valueMenu,
    viewAppOptions
  };
};
